import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEvents, createEvent, updateEvent, deleteEvent, uploadFlyer } from '../services/api';
import { Plus, CalendarDays, Pencil, Trash2, Users, Megaphone, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY_FORM = { name: '', description: '', event_date: '', location: '', flyer_url: '' };

function EventModal({ event, onClose, onSave }) {
  const [form, setForm] = useState(event ? {
    name: event.name, description: event.description || '',
    event_date: event.event_date ? event.event_date.slice(0, 16) : '',
    location: event.location || '', flyer_url: event.flyer_url || ''
  } : EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFlyer = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await uploadFlyer(file);
      set('flyer_url', data.url);
      toast.success('Flyer uploadé');
    } catch { toast.error("Erreur upload"); }
    finally { setUploading(false); }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Le nom est requis');
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{event ? 'Modifier' : 'Nouvel événement'}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nom de l'événement *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Mariage Ahmed & Sara" autoFocus />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Informations complémentaires..." rows={3} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Date & heure</label>
              <input type="datetime-local" value={form.event_date} onChange={e => set('event_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Lieu</label>
              <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="Salle des fêtes, Paris" />
            </div>
          </div>
          <div className="form-group">
            <label>Flyer (image d'invitation)</label>
            {form.flyer_url ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--bg3)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                <img src={form.flyer_url} alt="flyer" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }} />
                <span className="text-sm flex-1" style={{ wordBreak: 'break-all' }}>Flyer chargé</span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => set('flyer_url', '')}><X size={12} /></button>
              </div>
            ) : (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 13px', background: 'var(--bg3)', border: '1px dashed var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer' }}>
                {uploading ? <span className="spinner" /> : <Upload size={15} color="var(--text3)" />}
                <span className="text-muted text-sm">{uploading ? 'Upload en cours...' : 'Cliquer pour uploader (JPG, PNG, max 10MB)'}</span>
                <input type="file" accept="image/*" onChange={handleFlyer} style={{ display: 'none' }} />
              </label>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner" /> : (event ? 'Enregistrer' : 'Créer l\'événement')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | event object
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    getEvents()
      .then(r => setEvents(r.data))
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSave = async form => {
    if (modal?.id) {
      await updateEvent(modal.id, form);
      toast.success('Événement modifié');
    } else {
      await createEvent(form);
      toast.success('Événement créé');
    }
    load();
  };

  const handleDelete = async ev => {
    if (!window.confirm(`Supprimer "${ev.name}" et tous ses invités ?`)) return;
    await deleteEvent(ev.id);
    toast.success('Supprimé');
    load();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Événements</h1>
          <p className="text-muted">Gérez vos événements et leurs invités</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('create')}>
          <Plus size={15} /> Nouvel événement
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>
      ) : events.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <CalendarDays />
            <h3>Aucun événement</h3>
            <p>Créez votre premier événement pour démarrer.</p>
            <button className="btn btn-primary mt-4" onClick={() => setModal('create')}>
              <Plus size={15} /> Créer un événement
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {events.map(ev => (
            <div key={ev.id} className="card" style={{ cursor: 'pointer', transition: 'border-color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              {ev.flyer_url && (
                <img src={ev.flyer_url} alt="" style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 8, marginBottom: 16 }} />
              )}
              <div onClick={() => navigate(`/events/${ev.id}`)}>
                <h3 style={{ marginBottom: 4 }}>{ev.name}</h3>
                <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
                  {ev.event_date ? new Date(ev.event_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Date non définie'}
                  {ev.location ? ` · ${ev.location}` : ''}
                </p>
                <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{ev.total_invited || 0}</div>
                    <div className="text-sm text-muted">invités</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--green)' }}>{ev.confirmed || 0}</div>
                    <div className="text-sm text-muted">confirmés</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--red)' }}>{ev.declined || 0}</div>
                    <div className="text-sm text-muted">déclinés</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--amber)' }}>{ev.pending || 0}</div>
                    <div className="text-sm text-muted">en attente</div>
                  </div>
                </div>
              </div>
              <div className="divider" style={{ margin: '12px 0' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm flex-1" onClick={() => navigate(`/events/${ev.id}/guests`)}>
                  <Users size={13} /> Invités
                </button>
                <button className="btn btn-ghost btn-sm flex-1" onClick={() => navigate(`/events/${ev.id}/campaigns`)}>
                  <Megaphone size={13} /> Campagnes
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setModal(ev)}>
                  <Pencil size={13} />
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(ev)}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <EventModal
          event={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
